// Libs
import _ from "lodash";
import { oc } from "ts-optchain";

// Vue
import Vue from "vue";
import { mapGetters } from "vuex";

// Icons
import { faList, faPlus, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Services
import { ErrorLogService, ObjectUtil, ShoppingCartService, SettingsService, LiftLinkService, VisrService } from "@/services";

// Components
import { LoadingSpinner } from "@/components";
import { CartContentWrapper } from "@/components/Cart/CartContentWrapper";
import { PartsListGrid } from "@/components/Elements/PartsListGrid";
import { default as CartFreight } from "../Controls/CartFreight/CartFreight.vue";
import { default as CartPromos } from "../Controls/CartPromos/CartPromos.vue";
import { default as PromoCodes } from "../Controls/CartPromos/PromoCodes.vue";
import { default as CartTotals } from "../Controls/CartTotals/CartTotals.vue";
import { default as RetroFitForm } from "../Controls/LiftLinkForm/RetroFit/RetroFitForm.vue";
import { default as AddOnForm } from "../Controls/LiftLinkForm/AddOn/AddOnForm.vue";
import { default as SiteKitForm } from "../Controls/LiftLinkForm/SiteKit/SiteKitForm.vue";
import { default as LiftLinkLiteForm } from "../Controls/LiftLinkForm/LiftLinkLite/LiftLinkLiteForm.vue";
import { default as VisrTermsForm } from "../Controls/VisrTermsForm/VisrTermsForm.vue";
import { default as ClearCartConfirmationDialog } from "../Dialogs/ClearCartConfirmationDialog/ClearCartConfirmationDialog.vue";
import { default as RemovePartsConfirmation } from "../Dialogs/RemovePartsConfirmation/RemovePartsConfirmation.vue";
import { default as SaveAsPartsListDialog } from "../Dialogs/SaveAsPartsListDialog/SaveAsPartsListDialog.vue";
import { DownloadJsonToCsv } from "@/components/Elements/DownloadJsonToCsv"

// mixins
import { stringHelper } from "@/components/mixins";

// Types
import { IGridListRowItem } from "@/components/Elements/PartsListGrid/PartsListGrid";
import { IShoppingCartListResponseDto } from "@/types";
import { CartStepName, IShoppingCartContext, OrderValidationErrorReason, LiftLinkFormType } from "../Types";
import { constants } from "@/const"

export default Vue.extend({
  name: "Step1",
  components: {
    FontAwesomeIcon,
    ClearCartConfirmationDialog,
    RemovePartsConfirmation,
    SaveAsPartsListDialog,
    CartFreight,
    CartPromos,
    PromoCodes,
    CartTotals,
    CartContentWrapper,
    LoadingSpinner,
    PartsListGrid,
    DownloadJsonToCsv,
    RetroFitForm,
    AddOnForm,
    SiteKitForm,
    LiftLinkLiteForm,
    VisrTermsForm
  },
  mixins: [stringHelper],
  props: {
    context: {
      type: Object,
      default: () => ObjectUtil.createInstance<IShoppingCartContext>(),
    },
    validationErrorMessages: {
      type: Array,
      default: "",
    }
  },
  data: () => ({
    plusIcon: faPlus,
    listIcon: faList,
    cartIcon: faShoppingCart,

    activeView: "DEFAULT",

    busyMessage: "",
    isBusy: false,

    orderType: "",
    promoCode: "",

    showLocation: true,
    showDealerSku: true,
    showOEMPricing: false,
    ExxonInfo: null,
    ordersDisabled: false,
    ordersDisabledText: "",
    ordersDisabledNewStory: "",
    liftLinkFormComplete: false,
    liftLinkLiteFormComplete: false,
    liftLinkConfiguration: null,
    visrConfiguration: null,
    visrTermsAgreed: false
  }),
  computed: {
    ...mapGetters(["getContentItem", "hasPermission", "canPlaceOrders", "tenantDetails"]),

    items() {
      let mappedItems = oc(this.context as IShoppingCartContext).cartData.Items([]);
      mappedItems.forEach(element => {
        let errorMessages = this.validationErrorMessages.filter(x =>
          x.PartNumber == element.OemPartCode
        ).map(x =>
          `Item ${x.PartNumber} ${x.Message} (${x.PartNumber})`
        );

        element.newStockingLocation = element.StockingLocation
        element.newDealerSku = element.DealerSku

        // remove duplicate error messages
        element.ErrorMessages = Array.from(new Set(errorMessages));

        // show the warning message on the part
        if (element.ErrorMessages.length > 0) {
          element._showDetails = true
        }
      });
      return mappedItems;
    },

    cartIsEmpty() {
      return this.items.length === 0;
    },

    showOfflineHeader() {
      return this.context.showOfflineToWarehouseMessageHeader === true;
    },

    anyItemsSelected() {
      return oc(this.context as any)
        .cartData.Items([])
        .some((x: IGridListRowItem) => x.Selected);
    },

    isProceedButtonDisabled() {
      // Must come first: the override and offline paths below must not skip the terms
      if (this.showVisrTermsForm && !this.visrTermsAgreed) {
        return true;
      }

      if (this.ordersDisabled) {
        return true;
      }

      if (this.context.mixMaxValidationOverride) {
        return false;
      }

      if (this.context.isOfflineToSap || this.context.isOfflineToWarehouseSystems) {
        return false;
      }
      var llFormComplete = !(this.showAddOnForm || this.showSiteKitForm || this.showRetroFitForm) || this.liftLinkFormComplete
      var llLiteFormComplete = !(this.showLiftLinkLiteForm) || this.liftLinkLiteFormComplete
      let isDisabled = this.validationErrorMessages.length > 0 || !this.context.canProceedToStep2 || !this.canPlaceOrders || !llFormComplete || !llLiteFormComplete;
      return isDisabled
    },

    itemsToDownload() {
      let self = this;
      let returnItems = oc(this.context as IShoppingCartContext).cartData.Items([]).map(function (item) {
        return {
          DealerNetPartNumber: item.McfPartCode,
          Qty: item.Quantity,
          Oem: item.Oem.Id,
          OemPartNumber: item.OemPartCode,
          PartDescription: item.PartDescription,
          DealerBinLocation: item.StockingLocation,
          DealerSku: item.DealerSku,
          FleetList: self.formatToCurrency(
            item.FleetListPrice ? item.FleetListPrice : 0,
            self.context.billingCurrencyCode
              ? self.context.billingCurrencyCode
              : "USD"
          ),
          CoreCost: self.formatToCurrency(
            item.TotalCoreCost ? item.TotalCoreCost : 0,
            self.context.billingCurrencyCode
              ? self.context.billingCurrencyCode
              : "USD"
          ),
          UnitPrice: self.formatToCurrency(
            item.UnitPrice ? item.UnitPrice : 0,
            self.context.billingCurrencyCode
              ? self.context.billingCurrencyCode
              : "USD"
          ),
          TotalCost: self.formatToCurrency(
            item.TotalPrice ? item.TotalPrice : 0,
            self.context.billingCurrencyCode
              ? self.context.billingCurrencyCode
              : "USD"
          ),
        }
      })
      return returnItems;
    },

    canApplyPromoCode() {
      return !this.isNullOrWhiteSpace(this.promoCode);
    },

    validationSummaryHeading() {
      return this.getContentItem("ValidationSummaryHeading")
    },
    isPartsStoreOrder() {
      const ctx = this.context as IShoppingCartContext;
      return ctx.isPartsStoreOrder;
    },
    showRetroFitForm() {
      let cartItems = this.items;
      if (this.liftLinkConfiguration && this.liftLinkConfiguration.RetroFitFormPicCodes) {
        return _.intersection(this.liftLinkConfiguration.RetroFitFormPicCodes, cartItems.map(x => x.PicCode)).length > 0;
      }
    },
    showAddOnForm() {
      let cartItems = this.items;
      if (this.liftLinkConfiguration && this.liftLinkConfiguration.AddOnFormPicCodes) {

        return _.intersection(this.liftLinkConfiguration.AddOnFormPicCodes, cartItems.map(x => x.PicCode)).length > 0;
      }
    },
    showSiteKitForm() {
      let cartItems = this.items;
      if (this.liftLinkConfiguration && this.liftLinkConfiguration.SiteKitFormPicCodes) {
        return _.intersection(this.liftLinkConfiguration.SiteKitFormPicCodes, cartItems.map(x => x.PicCode)).length > 0;
      }
    },
    showLiftLinkLiteForm() {
      let cartItems = this.items;
      if (this.liftLinkConfiguration && this.liftLinkConfiguration.LiftLinkLiteFormPicCodes) {
        return _.intersection(this.liftLinkConfiguration.LiftLinkLiteFormPicCodes, cartItems.map(x => x.PicCode)).length > 0;
      }
    },
    showMixMaxOverrideButton() {
      return this.context?.cartData?.ValidationResults.ValidationErrors.filter(ve => ve.Reason.toString() === '19').length > 0;
    },
    showVisrTermsForm() {
      let cartItems = this.items;
      if (this.visrConfiguration && this.visrConfiguration.TermsAndConditionsPicCodes) {
        return _.intersection(this.visrConfiguration.TermsAndConditionsPicCodes, cartItems.map(x => x.PicCode)).length > 0;
      }
      return false;
    }
  },
  created() {
    this.liftLinkConfiguration = LiftLinkService.GetLiftLinkConfiguration().then(response => {
      this.liftLinkConfiguration = response;
    });

    this.visrConfiguration = VisrService.GetVisrConfiguration().then(response => {
      this.visrConfiguration = response;
    });
  },

  mounted() {
    const ctx = this.context as IShoppingCartContext;
    this.orderType = ctx.orderType;

    try {
      ShoppingCartService.isOrderingDisabled().then(response => {
        this.ordersDisabled = response;
        if (this.ordersDisabled) {
          SettingsService.getAllSettings().then(response => {
            var settings = response;
            var settingKeys = constants.MLA_ORDERS_BLACK_SETTINGS_KEYS;
            this.ordersDisabledNewStory = _.find(settings, (x) => x.Key == settingKeys.NEWS_STORY).Value;
            this.ordersDisabledText = _.find(settings, (x) => x.Key == settingKeys.MESSAGE).Value;
          });
        }
      });
    }
    catch {
      //Do nothing, allow orders
    }
  },
  methods: {

    navigateToStep(stepName: CartStepName) {
      this.$emit("on-step-change", stepName);
    },

    goToStep2() {
      if (this.showVisrTermsForm && !this.visrTermsAgreed) {
        return;
      }

      this.navigateToStep(CartStepName.Step2);
    },

    onItemsRemoving() {
      this.isBusy = true;
      this.busyMessage = "Removing items from cart...";
    },

    scrollToCartPart(partNumber) {
      var elem = document.getElementById(`partErrorMessage${partNumber}`);
      if (elem) {
        elem.scrollIntoView();
      }
    },

    onItemsRemoved(cartData: IShoppingCartListResponseDto) {
      try {
        this.$emit("cart-data-updated", cartData);

        this.isBusy = false;
        this.busyMessage = "";
      } finally {
        this.activeView = "DEFAULT";
      }
    },

    onCartClearing() {
      this.isBusy = true;
      this.busyMessage = "Clearing cart...";
    },

    onCartCleared(result: boolean) {
      this.isBusy = false;
      this.busyMessage = "";

      this.goToQuickOrders();
    },

    onItemsAddingToList() {
      this.isBusy = true;
      this.busyMessage = "Adding parts to list...";
    },

    onItemsAddedToList(result: boolean) {
      try {
        this.isBusy = false;
        this.busyMessage = "";
      } finally {
        this.activeView = "DEFAULT";
      }
    },

    overrideMinMaxValidation() {
      this.context.mixMaxValidationOverride = true;
    },

    rowUpdated(row: IGridListRowItem) {
      this.$emit("row-updated", row);
    },

    rowSelectionUpdated(row: IGridListRowItem) {
      this.$emit("row-selection-updated", row);
    },

    goToQuickOrders() {
      this.$router.push({ name: "QuickOrders" });
    },

    showSaveAsPartsListDialog() {
      this.activeView = "PARTS_LIST";
    },

    showRemovePartsConfirmation() {
      this.activeView = "REMOVE_PARTS";
    },

    showEmptyCartConfirmation() {
      this.activeView = "EMPTY_CART";
    },

    showDefaultView() {
      this.activeView = "DEFAULT";
    },

    orderTypeChanged() {
      ShoppingCartService.setOrderTypePref(this.orderType);

      this.$emit("order-type-changed", this.orderType);
    },

    submitApplyPromoCode() {
      if (!this.canApplyPromoCode) {
        this.promoCode = "";
        return;
      }

      let promoCodes = ShoppingCartService.getAppliedPromoCodes();

      promoCodes.push(this.promoCode);

      promoCodes = _.uniq(promoCodes);

      ShoppingCartService.setPromoCodes(promoCodes);

      this.$emit("promo-codes-changed", promoCodes);

      this.promoCode = "";
    },

    removePromo(promo: string) {
      let promoCodes = ShoppingCartService.getAppliedPromoCodes();

      promoCodes = promoCodes.filter(code => code !== promo.PromoCode);
      promoCodes = _.uniq(promoCodes);

      ShoppingCartService.setPromoCodes(promoCodes);

      this.$emit("promo-codes-changed", promoCodes);
    },

    showMessage(msg: string) {
      this.$store.commit("pushTimedMessage", {
        header: msg,
        IsError: false,
        seconds: 3,
      });
    },

    showError(err: any) {
      const msg = ErrorLogService.LogAndGetErrorMessage(err);

      this.$store.commit("pushTimedMessage", {
        header: msg,
        IsError: true,
        seconds: 15,
      });
    },

    llFormSubmitted(formData) {
      const ctx = this.context as IShoppingCartContext;
      ctx.liftLinkForm = formData;
      this.liftLinkFormComplete = true;
    },

    llFormDataInvalid() {
      this.liftLinkFormComplete = false;
    },

    getLLFormData() {
      const ctx = this.context as IShoppingCartContext;
      return ctx.liftLinkForm;
    },

    // LIFT LINK lite
    liftLinkLiteFormSubmitted(formData) {
      const ctx = this.context as IShoppingCartContext;
      ctx.liftLinkLiteForm = formData;
      this.liftLinkLiteFormComplete = true;
    },

    liftLinkLiteFormDataInvalid() {
      this.liftLinkLiteFormComplete = false;
    },

    getLiftLinkLiteFormData() {
      const ctx = this.context as IShoppingCartContext;
      return ctx.liftLinkLiteForm;
    },

    // VISR terms
    visrAgreementChanged(agreed: boolean) {
      this.visrTermsAgreed = agreed;
    }
  },
});
